import { isNonEmptyString, isNotUndefined } from '@wistia/type-guards';
import type { ChangeEvent, ComponentPropsWithoutRef, JSX, ReactNode } from 'react';
import { forwardRef, useId } from 'react';
import { styled, css } from 'styled-components';
import { visuallyHiddenStyle } from '../../css/visuallyHiddenStyle';
import { FormControlLabel } from '../../private/components/FormControlLabel';
import { useRadioGroup } from '../FormGroup/RadioGroup';

export type RadioSizeType = 'lg' | 'md' | 'sm';

const sizeSmall = css`
  --wui-radio-size: 14px;
  --wui-radio-icon-size: 7px;
`;

const sizeMedium = css`
  --wui-radio-size: 16px;
  --wui-radio-icon-size: 8px;
`;

const sizeLarge = css`
  --wui-radio-size: 20px;
  --wui-radio-icon-size: 10px;
`;

const getSizeCss = (size: RadioSizeType) => {
  if (size === 'sm') {
    return sizeSmall;
  }
  if (size === 'lg') {
    return sizeLarge;
  }
  return sizeMedium;
};

const StyledRadioWrapper = styled.div<{ $disabled: boolean; $hideLabel: boolean }>`
  --wui-radio-background-color: var(--wui-color-bg-surface);
  --wui-radio-border-color: var(--wui-color-border-secondary);
  --wui-radio-icon-color: transparent;
  --wui-radio-outline-color: transparent;

  &:hover {
    --wui-radio-border-color: var(--wui-color-border-secondary-hover);
  }

  &:has(input:checked) {
    --wui-radio-border-color: var(--wui-color-focus-ring);
    --wui-radio-icon-color: var(--wui-color-focus-ring);
  }

  &:has(input:focus-visible) {
    --wui-radio-outline-color: var(--wui-color-focus-ring);
  }

  &:has(input:disabled) {
    --wui-radio-background-color: var(--wui-color-bg-surface-disabled);
    --wui-radio-border-color: var(--wui-color-border-secondary);

    &:has(input:checked) {
      --wui-radio-icon-color: var(--wui-color-icon-disabled);
    }
  }

  display: flex;
  margin: 0;

  /* TODO this solves a problem but potentially causes and a11y issue */
  user-select: none;
`;

const StyledRadioInput = styled.div<{ $size: RadioSizeType }>`
  ${({ $size }) => getSizeCss($size)}
  width: var(--wui-radio-size);
  height: var(--wui-radio-size);
  min-width: var(--wui-radio-size);
  min-height: var(--wui-radio-size);
  background-color: var(--wui-radio-background-color);
  box-shadow: inset 0 0 0 1px var(--wui-radio-border-color);
  border-radius: 100%;
  position: relative;
  cursor: pointer;
  transition: box-shadow var(--wui-motion-duration-03) var(--wui-motion-ease);
  margin-top: calc(calc(1lh - var(--wui-radio-size)) / 2);

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 50%;
    height: 50%;
    background-color: var(--wui-radio-icon-color);
    border-radius: 100%;
    transform: translate(-50%, -50%);
  }
`;

const StyledHiddenRadioInput = styled.input`
  ${visuallyHiddenStyle}
`;

export type RadioProps = Omit<
  ComponentPropsWithoutRef<'input'>,
  'hideLabel' | 'label' | 'onChange' | 'size' | 'value'
> & {
  /**
   * Indicates the state of the radio
   */
  checked?: boolean;
  /**
   * Disables input
   */
  disabled?: boolean;
  /**
   * Indicates that there is an error with the input
   */
  'aria-invalid'?: boolean;
  /**
   * Provides an ID for the input
   */
  id?: string;
  /**
   * The label for the input
   */
  label: ReactNode;
  /**
   * The description for the radio
   */
  description?: ReactNode | undefined;
  /**
   * Should match the ID attribute of the radio.
   * Can be used to group together multiple radios
   */
  name?: string;
  /**
   * Callback function invoked when the value changes
   */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /**
   * Indicates if the input is required
   */
  required?: boolean;
  /**
   * A predefined size for the radio
   */
  size?: RadioSizeType;
  /**
   * The value attribute of the input
   */
  value?: string;
  /**
   * Whether to visually hide the label (it will still be available to screen readers)
   */
  hideLabel?: boolean;
};

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      checked,
      disabled = false,
      id,
      label,
      description,
      name,
      onChange,
      size = 'md',
      value = 'false',
      required = false,
      hideLabel = false,
      ...props
    }: RadioProps,
    ref,
  ): JSX.Element => {
    const generatedId = useId();
    const computedId = isNonEmptyString(id) ? id : `wistia-ui-radio-${generatedId}`;
    const radioGroupContext = useRadioGroup();

    // use context values if provided, otherwise fall back to the props
    const contextName = radioGroupContext?.name;
    const contextOnChange = radioGroupContext?.onChange;
    const contextValue = radioGroupContext?.value;
    const radioName = name ?? contextName;
    const handleOnChange = onChange ?? contextOnChange;
    const isChecked =
      isNotUndefined(value) && isNotUndefined(contextValue) ? contextValue === value : checked;

    return (
      <StyledRadioWrapper
        $disabled={disabled}
        $hideLabel={hideLabel}
        aria-invalid={props['aria-invalid']}
      >
        <StyledHiddenRadioInput
          {...props}
          ref={ref}
          checked={isChecked}
          disabled={disabled}
          id={computedId}
          name={radioName}
          onChange={handleOnChange}
          required={required}
          type="radio"
          value={value}
        />
        <FormControlLabel
          controlSlot={<StyledRadioInput $size={size} />}
          description={description}
          disabled={disabled}
          hideLabel={hideLabel}
          htmlFor={computedId}
          label={label}
        />
      </StyledRadioWrapper>
    );
  },
);

Radio.displayName = 'Radio';
